import { attributesToProps, type Element as HtmlParserElement } from 'html-react-parser';
import type { JSX, ScriptHTMLAttributes } from 'react';
import { cn } from '@/app_lib/utils/utils';

type ScriptDomProps = ScriptHTMLAttributes<HTMLScriptElement>;

function shouldSkipOneTrustAutoblockForScript(attribs: Record<string, string | undefined>): boolean {
  const rawType = (attribs.type || '').toLowerCase();
  if (rawType === 'application/ld+json' || rawType === 'application/json') {
    return true;
  }
  const cls = attribs.class || '';
  if (cls.includes('optanon-category-')) {
    return true;
  }
  return false;
}

function getScriptTextContentFromHtmlElement(domNode: HtmlParserElement): string {
  return (
    domNode.children
      ?.map((child) => {
        if ('data' in child && typeof (child as { data?: string }).data === 'string') {
          return (child as { data: string }).data;
        }
        return '';
      })
      .join('') ?? ''
  );
}

/**
 * OneTrust AutoBlock category class aligned with app `personalizedAds` (Targeting C0004 + Social C0005).
 * External scripts use data-src; OneTrust promotes to a live script after consent.
 * @see docs/COOKIE_CONSENT_AND_TRACKING.md
 */
const CMS_SCRIPT_ONETRUST_CATEGORY_CLASS = 'optanon-category-C0004-C0005';

/**
 * Maps WordPress/CMS <script> nodes to DOM markup suitable for OneTrust AutoBlock.
 * Skips gating for JSON-LD and scripts already tagged with optanon-category-*.
 */
function cmsHtmlScriptToReactElement(
  attribs: Record<string, string>,
  inlineBody: string | undefined,
  key?: string | number,
): JSX.Element {
  if (shouldSkipOneTrustAutoblockForScript(attribs)) {
    const props = attributesToProps(attribs) as ScriptDomProps;
    if (inlineBody !== undefined && inlineBody !== '') {
      return (
        <script
          {...props}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: Inline CMS scripts are rendered inert or as JSON-LD; required to preserve markup
          dangerouslySetInnerHTML={{ __html: inlineBody }}
          key={key}
          suppressHydrationWarning
        />
      );
    }
    return (
      <script
        {...props}
        key={key}
        suppressHydrationWarning
      />
    );
  }

  const props = attributesToProps(attribs) as Record<string, unknown>;
  const src = typeof props.src === 'string' ? props.src : undefined;
  const { src: _omitSrc, className, children: _omitChildren, dangerouslySetInnerHTML: _omitDsh, ...rest } = props;
  const mergedClass = cn(CMS_SCRIPT_ONETRUST_CATEGORY_CLASS, className);

  if (src) {
    return (
      <script
        {...(rest as ScriptDomProps)}
        className={mergedClass}
        data-src={src}
        key={key}
        suppressHydrationWarning
        type='text/plain'
      />
    );
  }

  const body = inlineBody ?? '';
  return (
    <script
      {...(rest as ScriptDomProps)}
      className={mergedClass}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: CMS inline script stored as text/plain until OneTrust promotes to executable script
      dangerouslySetInnerHTML={{ __html: body }}
      key={key}
      suppressHydrationWarning
      type='text/plain'
    />
  );
}

export {
  CMS_SCRIPT_ONETRUST_CATEGORY_CLASS,
  cmsHtmlScriptToReactElement,
  getScriptTextContentFromHtmlElement,
  shouldSkipOneTrustAutoblockForScript,
};
